/**
 * AdminRow.path / AdminTable.path の解決。
 *
 * 「機器の状態のどこを見るか」をドット区切りで表す。
 *   'ip.address' / 'wireless.channel' / 'ports.0.adminUp' / 'firewallRules'
 * 配列の添字も同じ書き方で辿れる。
 *
 * **ここで読んだ値をそのまま画面に出す。UI 側で値を持たない。**
 */

/** ドット区切りのパスを 1 段ずつ辿る。途中で見つからなければ undefined */
export function readPath(root: unknown, path: string): unknown {
  const segments = path
    .split('.')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
  return step(root, segments);
}

function step(current: unknown, segments: string[]): unknown {
  if (segments.length === 0) return current;
  if (current === null || current === undefined) return undefined;

  if (Array.isArray(current)) {
    const [head, ...rest] = segments;
    // 'ports.length' のように件数を出したい場合がある
    if (head === 'length') return step(current.length, rest);
    const index = Number(head);
    if (!Number.isInteger(index)) return undefined;
    return step(current[index], rest);
  }

  if (typeof current !== 'object') return undefined;
  const record = current as Record<string, unknown>;

  // キー自体がドットを含むことがある（DNS のホスト名 'portal.mirai-shoji.co.jp' など）。
  // 長い方から一致を試すことで、'dnsRecords.portal.mirai-shoji.co.jp' も辿れる。
  for (let length = segments.length; length >= 1; length--) {
    const key = segments.slice(0, length).join('.');
    if (Object.prototype.hasOwnProperty.call(record, key)) {
      return step(record[key], segments.slice(length));
    }
  }
  return undefined;
}

/** 表の 1 行から列の値を読む（列の field もドット区切りを許す） */
export function readField(row: unknown, field: string): unknown {
  return readPath(row, field);
}

/** 画面に出す文字列にする。真偽値と null は日本語にして初心者が読めるようにする */
export function formatValue(value: unknown): string {
  if (value === null) return '（未設定）';
  if (value === undefined) return '—';
  if (typeof value === 'boolean') return value ? '有効' : '無効';
  if (Array.isArray(value)) return value.length === 0 ? '（なし）' : value.map(formatValue).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/**
 * 表として読み出す（表の描画用）。
 *
 * 配列（firewallRules / ports）はそのまま。
 * 「名前 → 値」の対応表（DNS の dnsRecords）はオブジェクトなので、
 * 1 件を `{ key, value }` の行にして返す。
 * admin-screens.json の DNS 画面が columns に field: "key" / "value" を
 * 指定しているのはこの形を前提にしている。
 * どちらでもなければ空（表は「項目がありません。」になる）。
 */
export function readArray(root: unknown, path: string): unknown[] {
  const value = readPath(root, path);
  if (Array.isArray(value)) return value;
  if (value !== null && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return Object.keys(record).map((key) => ({ key, value: record[key] }));
  }
  return [];
}
